import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface CommentSummaryModel {
  summary: string;
  sentiment: 'positive' | 'neutral' | 'negative';
  keyTakeaways: string[];
}

export interface InteractionSummaryModel {
  summary: string;
  highlights: string[];
  totals: {
    questions: number;
    polls: number;
    comments: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AiService {
  private baseUrlAi = `${environment.baseUrl}/ai`;

  constructor(private http: HttpClient) {}

  // ─── Feature 1: Generate Summary (Tangram + Media pages) ──────

  getTangramSummary(pathwayId: string, provider: 'gemini' | 'openai' = 'gemini'): any {
    return this.http.get(`${this.baseUrlAi}/summary/tangram/${pathwayId}`, { params: { provider } });
  }

  getMediaSummary(videoId: string, provider: 'gemini' | 'openai' = 'gemini'): any {
    return this.http.get(`${this.baseUrlAi}/summary/media/${videoId}`, { params: { provider } });
  }

  // ─── Feature 2: AI Suggest (Tangram + Media pages) ───────────

  getMediaSuggestions(videoId: string, provider: 'gemini' | 'openai' = 'gemini'): any {
    return this.http.get(`${this.baseUrlAi}/suggest/media/${videoId}`, { params: { provider } });
  }

  getTangramSuggestions(pathwayId: string, provider: 'gemini' | 'openai' = 'gemini'): any {
    return this.http.get(`${this.baseUrlAi}/suggest/tangram/${pathwayId}`, { params: { provider } });
  }

  // ─── Feature 3: AI Chat (Both pages) ─────────────────────────

  sendChatMessage(data: {
    message: string;
    pathwayId?: string;
    videoId?: string;
    provider?: string;
    messages?: Array<{ role: string; content: string }>;
  }): any {
    return this.http.post(`${this.baseUrlAi}/chat`, data);
  }

  getSimilarContent(seed: string, context: { pathwayId?: string; videoId?: string; mode?: string } = {}): Observable<any> {
    const payload = { seed, ...context };
    return this.http.post(`${this.baseUrlAi}/similar-content`, payload).pipe(
      catchError(() => this.http.post(`${this.baseUrlAi}/suggest/similar-content`, payload)),
      catchError(() => of({ success: true, data: { items: [] } }))
    );
  }

  getContentRecommendations(payload: { input: string; mode: string; pathwayId?: string; videoId?: string }): Observable<any> {
    return this.http.post(`${this.baseUrlAi}/recommendations/content`, payload).pipe(
      catchError(() => this.http.post(`${this.baseUrlAi}/recommend/content`, payload)),
      catchError(() => of({ success: true, data: { recommendations: [] } }))
    );
  }

  getCuratorAssistantDraft(payload: { input: string; mode: string; pathwayId?: string }): any {
    return this.http.post(`${this.baseUrlAi}/curator-assistant`, payload);
  }

  getSimilarTangrams(payload: { pathwayId?: string; input?: string; limit?: number }): Observable<any> {
    return this.http.post(`${this.baseUrlAi}/similar-tangrams`, payload).pipe(
      catchError(() => this.http.post(`${this.baseUrlAi}/discover/similar-tangrams`, payload)),
      catchError(() => of({ success: true, data: { items: [] } }))
    );
  }

  getContentOverview(payload: { pathwayId?: string; videoId?: string; provider?: 'gemini' | 'openai' }): Observable<any> {
    return this.http.post(`${this.baseUrlAi}/overview`, payload).pipe(
      catchError(() => this.http.post(`${this.baseUrlAi}/summary/content-overview`, payload)),
      catchError(() => of({ success: true, data: { overview: '' } }))
    );
  }

  enhanceText(payload: {
    text: string;
    purpose: 'title' | 'description' | 'audience-tuning' | 'general';
    audience?: string;
    tone?: string;
    provider?: 'gemini' | 'openai';
  }): Observable<any> {
    return this.http.post(`${this.baseUrlAi}/enhance-text`, payload).pipe(
      catchError(() => this.http.post(`${this.baseUrlAi}/content/enhance`, payload)),
      catchError(() => of({ success: true, data: { text: payload.text } }))
    );
  }

  getLearningRoadmap(payload: any, provider: 'gemini' | 'openai' = 'gemini'): any {
    return this.http.post(`${this.baseUrlAi}/roadmap`, payload, { params: { provider } });
  }

  getCommentSummary(
    payload: { pathwayId: string; videoId?: string; comments: string[]; provider?: 'gemini' | 'openai' },
    provider: 'gemini' | 'openai' = 'gemini'
  ): Observable<CommentSummaryModel | null> {
    // Backend endpoint /ai/summary/comments is not deployed yet — short-circuit to null.
    return of(null);
    // return this.http.post(`${this.baseUrlAi}/summary/comments`, payload, { params: { provider } }).pipe(
    //   map((res: any) => {
    //     const data = (res && res.data) || {};
    //     if (!data || !data.summary) {
    //       return null;
    //     }
    //     return {
    //       summary: data.summary,
    //       sentiment: this.normalizeSentiment(data.sentiment),
    //       keyTakeaways: Array.isArray(data.keyTakeaways) ? data.keyTakeaways.slice(0, 3) : []
    //     } as CommentSummaryModel;
    //   }),
    //   catchError(() => of(null))
    // );
  }

  getInteractionSummary(
    payload: { pathwayId: string; videoId?: string; questions: any[]; polls: any[]; commentCount: number; provider?: 'gemini' | 'openai' },
    provider: 'gemini' | 'openai' = 'gemini'
  ): Observable<InteractionSummaryModel | null> {
    // Backend endpoint /ai/summary/interactions is not deployed yet — short-circuit to null.
    return of(null);
    // return this.http.post(`${this.baseUrlAi}/summary/interactions`, payload, { params: { provider } }).pipe(
    //   map((res: any) => {
    //     const data = (res && res.data) || {};
    //     if (!data || !data.summary) {
    //       return null;
    //     }
    //     return {
    //       summary: data.summary,
    //       highlights: Array.isArray(data.highlights) ? data.highlights.slice(0, 3) : [],
    //       totals: {
    //         questions: Number((data.totals && data.totals.questions) || 0),
    //         polls: Number((data.totals && data.totals.polls) || 0),
    //         comments: Number((data.totals && data.totals.comments) || 0)
    //       }
    //     } as InteractionSummaryModel;
    //   }),
    //   catchError(() => of(null))
    // );
  }

  private normalizeSentiment(sentiment: any): 'positive' | 'neutral' | 'negative' {
    const normalized = String(sentiment || '').toLowerCase();
    if (normalized === 'positive' || normalized === 'negative') {
      return normalized;
    }
    return 'neutral';
  }

  // ─── Feature 4: Document Extraction ──────────────────────────

  /**
   * Upload a document for analysis. Returns either a plan (stage="plan_ready")
   * or clarification questions (stage="clarification_required"). The optional
   * userComment lets the user steer the plan up front — when supplied, the
   * backend skips the clarification branch and returns a plan directly.
   */
  extractDocument(file: File, provider: 'gemini' | 'openai' = 'gemini', userComment: string = '') {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('provider', provider);
    if (userComment && userComment.trim()) {
      formData.append('userComment', userComment.trim());
    }
    return this.http.post(`${this.baseUrlAi}/extract-document`, formData);
  }

  /**
   * Second step of the AI-upload flow: submit the clarification answers (and
   * any refined user comment) so the backend can produce the final plan
   * without re-extracting the document text.
   */
  finalizePlan(data: {
    documentText: string;
    filename?: string;
    userComment?: string;
    clarificationAnswers?: { [questionId: string]: string };
    provider?: 'gemini' | 'openai';
  }) {
    return this.http.post(`${this.baseUrlAi}/finalize-plan`, data);
  }

  // ─── Feature 5: Create Draft Tangram from Document ───────────

  createDraftFromDocument(data: {
    tangramTitle: string;
    tangramDescription: string;
    sections: Array<{
      title: string;
      description: string;
      videoURL: string;
      type: string;
      order: number;
    }>;
  }) {
    return this.http.post(`${this.baseUrlAi}/create-draft-from-document`, data);
  }
}
